import { Box, Text, Heading, Button, CloseButton } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import { SparklesFilled as SparklesFilledIcon } from '@wix/wix-ui-icons-common';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import React, {
  FunctionComponent,
  useEffect,
  useContext,
  useState,
} from 'react';

import { useKeraAssistantBI } from './bi';
import { KeraAssistantPermissionModal } from './KeraAssistantPermissionModal';
import { useStreaming } from './useStreaming';
import { Context } from '../../../context';
import { DATA_HOOKS } from '../../../dataHooks';
import { getRedirectToLoginUrl } from '../../../utils/login';
import { useStore } from '../../../zustand/stores';

import css from './index.module.scss';

export interface KeraAssistantProps {
  articleId: string;
  articleUrl: string;
}

export const KeraAssistant: FunctionComponent<KeraAssistantProps> = ({
  articleUrl,
  articleId,
}) => {
  const { t } = useTranslation();
  const {
    sessionBI: { selfServiceSessionId },
    siteSelectorData,
  } = useContext(Context);
  const { user } = useStore((state) => state.userSlice);
  const {
    onKeraAssistantClick,
    onKeraAssistantExposure,
    onKeraAssistantClose,
    onConfirmationModalLoad,
    onConfirmationModalApprove,
    onConfirmationModalClose,
  } = useKeraAssistantBI(articleId, selfServiceSessionId || undefined);
  const [isVisible, setIsVisible] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const {
    state: { isStreaming },
    setConfig,
    startStreaming,
    stopStreaming,
  } = useStreaming();

  const { locale } = useRouter();

  const titleKey =
    user !== null ? 'kera-assistant.title' : 'kera-assistant.login-title';
  const descriptionKey =
    user !== null
      ? 'kera-assistant.description'
      : 'kera-assistant.login-description';
  const ctaKey =
    user !== null
      ? 'kera-assistant.continue-to-kera'
      : 'kera-assistant.login-button';

  useEffect(() => {
    setConfig({
      userName: `${user?.firstName}`,
      articleUrls: [articleUrl],
      lastEditedSiteName:
        siteSelectorData.selectedSite?.siteProperties.siteDisplayName || '',
      userId: user?.userId || '',
    });
  }, [user, siteSelectorData, articleUrl, setConfig]);

  useEffect(() => {
    void onKeraAssistantExposure();
  }, [onKeraAssistantExposure]);

  const handleTryButtonClick = async () => {
    if (user === null) {
      const loginUrl = getRedirectToLoginUrl(locale as string, true);
      window.location.href = loginUrl;
      return;
    }

    if (!isStreaming) {
      setShowPermissionModal(true);
    } else {
      stopStreaming();
    }

    await onConfirmationModalLoad();
  };

  const handlePermissionContinue = async () => {
    setShowPermissionModal(false);
    await onConfirmationModalApprove();
    await startStreaming();
    await onKeraAssistantClick(t(ctaKey));
  };

  const handlePermissionCancel = async () => {
    setShowPermissionModal(false);
    await onConfirmationModalClose();
  };

  const handleCloseClick = async () => {
    await onKeraAssistantClose();
    setIsClosing(true);
    setTimeout(() => setIsVisible(false), 150);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Box
        className={classNames(css.wrapper, isClosing ? css.closing : '')}
        dataHook={DATA_HOOKS.KERA_ASSISTANT}
        borderRadius="18px"
        position="relative"
        width="100%"
        padding="1px"
      >
        <Box
          className={css.content}
          padding="18px"
          width="100%"
          align="space-between"
        >
          <Box className={css.textWrapper} direction="vertical">
            <Box className={css.header}>
              <Heading
                appearance="H3"
                className={css.title}
                dataHook={DATA_HOOKS.KERA_ASSISTANT_TITLE}
              >
                {t(titleKey)}
              </Heading>
            </Box>
            <Text
              size="medium"
              className={css.description}
              dataHook={DATA_HOOKS.KERA_ASSISTANT_DESCRIPTION}
            >
              {t(descriptionKey)}
            </Text>
          </Box>
          <Box className={css.actions} verticalAlign="bottom">
            <Button
              prefixIcon={<SparklesFilledIcon />}
              size="medium"
              weight="normal"
              skin="ai"
              className={css.tryButton}
              dataHook={DATA_HOOKS.KERA_ASSISTANT_TRY_BUTTON}
              onClick={handleTryButtonClick}
            >
              {isStreaming ? t('kera-assistant.stop-sharing') : t(ctaKey)}
            </Button>
          </Box>
        </Box>
        <CloseButton
          skin="dark"
          size="medium"
          className={css.closeButton}
          onClick={handleCloseClick}
          dataHook={DATA_HOOKS.KERA_ASSISTANT_CLOSE_BUTTON}
        />
      </Box>

      <KeraAssistantPermissionModal
        isOpen={showPermissionModal}
        onCancel={handlePermissionCancel}
        onContinue={handlePermissionContinue}
      />
    </>
  );
};

export default KeraAssistant;
